import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import type { SessionUser } from '@/types'

export interface AuthState {
  // State
  user: SessionUser | null
  isAuthenticated: boolean
  forcePasswordChange: boolean
  // True once a user signed in during this page load or the stored session. Not cleared by logout/expiry,
  // so ProtectedRoute remembers the page to return to only on a fresh visit.
  hadSession: boolean

  // Actions
  setAuth: (params: {
    user: SessionUser
    forcePasswordChange?: boolean
  }) => void
  clearAuth: () => void
  clearUser: () => void
  updateUser: (user: Partial<SessionUser>) => void
  setForcePasswordChange: (value: boolean) => void
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      // Initial state
      user: null,
      isAuthenticated: false,
      forcePasswordChange: false,
      hadSession: false,

      // Set auth state after successful login (cookies hold the actual session)
      setAuth: ({ user, forcePasswordChange = false }) => {
        if (import.meta.env.DEV) {
          console.info('[Auth] Setting auth state', {
            email: user.email,
            forcePasswordChange,
          })
        }

        set({
          user,
          isAuthenticated: true,
          forcePasswordChange,
          hadSession: true,
        })
      },

      // Clear all auth data (full logout)
      clearAuth: () => {
        if (import.meta.env.DEV) {
          console.info('[Auth] Clearing auth state')
        }
        set({
          user: null,
          isAuthenticated: false,
          forcePasswordChange: false,
        })
      },

      // Legacy: Clear user data (backwards compatibility)
      clearUser: () =>
        set({
          user: null,
          isAuthenticated: false,
          forcePasswordChange: false,
        }),

      // Update user data (e.g., after profile update)
      updateUser: (userData: Partial<SessionUser>) =>
        set((state) => ({
          user: state.user ? { ...state.user, ...userData } : null,
        })),

      // Update force password change flag
      setForcePasswordChange: (value: boolean) => {
        if (import.meta.env.DEV) {
          console.info('[Auth] Setting forcePasswordChange:', value)
        }
        set({ forcePasswordChange: value })
      },
    }),
    {
      name: 'auth-storage',
      version: 1,
      storage: createJSONStorage(() => localStorage),
      // A stored signed-in session counts as having had one (a reload must not turn expiry into a fresh visit).
      merge: (persisted, current) => ({
        ...current,
        ...(persisted as Partial<AuthState>),
        hadSession: !!(persisted as Partial<AuthState> | undefined)?.isAuthenticated,
      }),
      // Drop stale token fields persisted by v0 (pre-cookie-auth) clients.
      migrate: (persistedState, version) => {
        if (version === 0 && persistedState && typeof persistedState === 'object') {
          const next = { ...(persistedState as Record<string, unknown>) }
          delete next.token
          delete next.refreshToken
          delete next.tokenExpiresAt
          return next as unknown as AuthState
        }
        return persistedState as AuthState
      },
      partialize: (state) => ({
        // Only persist these fields
        user: state.user,
        isAuthenticated: state.isAuthenticated,
        forcePasswordChange: state.forcePasswordChange,
      }),
    }
  )
)
